import { Box, Paper, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';

const describeAttachment = (attachment, showsDocumentRubric, linkedRubric, linkedNumber) => {
  if (showsDocumentRubric) {
    return linkedRubric ? `Rubrique n°${linkedNumber} du document` : 'Rubrique retirée du document';
  }
  if (attachment.kind === 'standard') {
    const count = attachment.rubrics?.length || 0;
    return `Canevas · ${count} rubrique${count > 1 ? 's' : ''}`;
  }
  return attachment.rubrics?.[0]?.generated === false ? 'Rubrique à générer' : 'Rubrique';
};

/**
 * Encadré publié dans la discussion par glisser-déposer.
 * rubric / standard : bouton + (ajout en fin de document), et glissable vers l'édition ou le sommaire.
 * reportRubric : rubrique du document, l'œil la montre ; linkedRubric null si elle a été supprimée depuis.
 * rubric déjà ajoutée (addedRubricId) : présentée comme la rubrique du document tant qu'elle existe,
 * puis à nouveau comme rubrique à ajouter si elle en est retirée.
 */
function ChatAttachmentCard({
  attachment,
  linkedRubric,
  linkedNumber,
  isCurrentUser,
  isDark,
  adding,
  onAdd,
  onView,
  onDragStart,
  borderRadius
}) {
  const isAddedRubric = attachment.kind === 'rubric' && Boolean(attachment.addedRubricId && linkedRubric);
  const showsDocumentRubric = attachment.kind === 'reportRubric' || isAddedRubric;
  const canDrag = !showsDocumentRubric && !adding;
  const accent = isDark ? '#4fc3f7' : '#1976d2';
  const Icon = attachment.kind === 'standard'
    ? LibraryBooksOutlinedIcon
    : (showsDocumentRubric ? ArticleOutlinedIcon : NoteAddOutlinedIcon);
  const title = showsDocumentRubric ? (linkedRubric?.title || attachment.title) : attachment.title;

  return (
    <Paper
      elevation={0}
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      title={canDrag ? 'Glisser dans le document ou le sommaire' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        pr: 0.5,
        pl: canDrag ? 0.5 : 1.25,
        borderRadius: borderRadius || (isCurrentUser ? '14px 4px 14px 14px' : '4px 14px 14px 14px'),
        border: '1px solid',
        borderColor: isCurrentUser
          ? (isDark ? 'rgba(79, 195, 247, 0.45)' : 'rgba(25, 118, 210, 0.35)')
          : (isDark ? '#2d3f4c' : 'rgba(0, 0, 0, 0.08)'),
        borderLeft: `4px solid ${accent}`,
        bgcolor: isDark ? '#22303e' : '#ffffff',
        color: isDark ? '#ecf0f1' : 'text.primary',
        boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.3)' : '0 1px 2px rgba(0,0,0,0.08)',
        cursor: canDrag ? 'grab' : 'default',
        opacity: showsDocumentRubric && !linkedRubric ? 0.7 : 1,
        transition: 'box-shadow 0.2s ease',
        ...(canDrag && {
          '&:hover': {
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.45)' : '0 2px 8px rgba(0,0,0,0.12)'
          },
          '&:active': { cursor: 'grabbing' }
        })
      }}
    >
      {canDrag && (
        <DragIndicatorIcon sx={{ fontSize: 16, flexShrink: 0, color: isDark ? '#95a5a6' : 'text.secondary' }} />
      )}
      <Box sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(25, 118, 210, 0.08)',
        color: accent
      }}>
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {title}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: isDark ? '#95a5a6' : 'text.secondary' }}>
          {describeAttachment(attachment, showsDocumentRubric, linkedRubric, linkedNumber)}
        </Typography>
      </Box>
      {showsDocumentRubric ? (
        <Tooltip title={linkedRubric ? 'Voir cette rubrique' : 'Cette rubrique ne fait plus partie du document'}>
          <span>
            <IconButton
              size="small"
              onClick={onView}
              disabled={!linkedRubric}
              aria-label="Voir cette rubrique"
              sx={{ color: isDark ? '#bdc3c7' : undefined }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={attachment.kind === 'standard' ? 'Ajouter le canevas à la fin du document' : 'Ajouter à la fin du document'}>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={onAdd}
              disabled={adding}
              aria-label="Ajouter à la fin du document"
              sx={{ color: isDark ? '#4fc3f7' : undefined }}
            >
              {adding ? <CircularProgress size={16} /> : <AddCircleOutlineIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Paper>
  );
}

export default ChatAttachmentCard;
